import type { InteractiveElementScore } from "@/lib/interactiveElementState";

/**
 * The quiz subtypes rendered by the dedicated `QuizCode*Flow` components. These
 * track learning progress off the first validation (rather than scroll-into-view
 * like other content elements), so a few places need to recognise them:
 *
 *  - `CODE_ASSERTION`, `CODE_DEBUG`, `CODE_EXPLANATION` — server-validated,
 *    durable state in `validation_data`.
 *  - `CODE_TRUE_FALSE`, `CODE_FREETEXT` — client-validated, local interactive state.
 *
 * `CODE_MULTIPLE_CHOICE` is intentionally excluded — it renders through the
 * regular `QuizMultipleChoiceFlow` and behaves like the other quizzes.
 */
export const CODE_QUIZ_SUBTYPES = new Set<string>([
  "CODE_ASSERTION",
  "CODE_DEBUG",
  "CODE_EXPLANATION",
  "CODE_TRUE_FALSE",
  "CODE_FREETEXT",
]);

/**
 * Subtypes that fill the page under the header in the course completion quiz.
 * The code ones among them open the wide overlay in a lesson. `CODE_TRUE_FALSE`
 * stays in the inline card, same as in a lesson.
 */
export const WIDE_QUIZ_SUBTYPES = new Set<string>([
  "CODE_ASSERTION",
  "CODE_DEBUG",
  "CODE_EXPLANATION",
  "CODE_FREETEXT",
]);

export const isCodeQuiz = (contentType: string, subType: string): boolean =>
  contentType === "QUIZ" && CODE_QUIZ_SUBTYPES.has(subType);

/** True when this question should fill the page instead of the inline card. */
export const isWideQuiz = (subType: string): boolean =>
  WIDE_QUIZ_SUBTYPES.has(subType);

/** Converts a `{correct, total}` score into a 0–100 percentage for the server. */
export const scoreToPercent = (
  score?: InteractiveElementScore
): number | undefined => {
  if (!score || score.total <= 0) return undefined;
  return Math.round((score.correct / score.total) * 100);
};
